// TODO: Check if all usage is from Points and Vectors, eliminate these functions

import { GeomUtils, ICoord2D } from './geom-utils';

export function GetSqrMag(pointA: ICoord2D, pointB: ICoord2D): number {
  const deltaX = pointA.x - pointB.x;
  const deltaY = pointA.y - pointB.y;

  return (deltaX * deltaX) + (deltaY * deltaY);
}

// ! Avoid where possible
export function GetMag(pointA: ICoord2D, pointB: ICoord2D): number {
  return Math.sqrt(GetSqrMag(pointA, pointB));
}

const rad2DegMultiplier = 180 / Math.PI;

export function Rad2Deg(radians: number): number {
  return (radians * rad2DegMultiplier) + (radians > 0 ? 0 : 360);
}

const deg2RadMultiplier = Math.PI / 180;

export function Deg2Rad(degrees: number): number {
  if (degrees > 180) { degrees -= 360; }

  return degrees * deg2RadMultiplier;
}

export function ConstrainAngle(angleRad: number, angleLimit: number) {
  let angleDeg = Rad2Deg(angleRad);

  // All angles 0 to 360 are converted to be -90 to +90, to allow for constraint and easy radian conversion/usage.
  if (angleDeg > 90) {
    if (angleDeg >= 270) {
      angleDeg -= 360;
    }
    else {
      angleDeg -= 180;
    }
  }

  const constrainedAngleDeg = Math.min(Math.max(angleDeg, -angleLimit), angleLimit);

  return Deg2Rad(constrainedAngleDeg);
}

export function ClosestPointOnSegment(a: ICoord2D, b: ICoord2D, p: ICoord2D): ICoord2D {
  const ap = { x: p.x - a.x, y: p.y - a.y };
  const ab = { x: b.x - a.x, y: b.y - a.y };
  const abLenSq = ab.x ** 2 + ab.y ** 2;
  const ap_ab = ap.x * ab.x + ap.y * ab.y;
  const t = Math.max(0, Math.min(1, ap_ab / abLenSq));

  return { x: a.x + ab.x * t, y: a.y + ab.y * t };
}

export function ClosestPointOnPolygon(target: ICoord2D, polygon: ICoord2D[]): ICoord2D {
  let closest: ICoord2D = { x: 0, y: 0 };
  let minDist = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const point = ClosestPointOnSegment(a, b, target);
    const distSq = (point.x - target.x) ** 2 + (point.y - target.y) ** 2;

    if (distSq < minDist) {
      minDist = distSq;
      closest = point;
    }
  }

  return closest;
}

export function GetRaySegmentIntersection(rayStart: ICoord2D, rayEnd: ICoord2D, segStart: ICoord2D, segEnd: ICoord2D): ICoord2D | null {
  const r_px = rayEnd.x - rayStart.x;
  const r_py = rayEnd.y - rayStart.y;
  const s_px = segEnd.x - segStart.x;
  const s_py = segEnd.y - segStart.y;

  const denominator = r_px * s_py - r_py * s_px;
  if (denominator === 0) { return null; } // Lines are parallel and don't intersect

  const t = ((segStart.x - rayStart.x) * s_py - (segStart.y - rayStart.y) * s_px) / denominator;
  const u = ((segStart.x - rayStart.x) * r_py - (segStart.y - rayStart.y) * r_px) / denominator;

  if (t >= 0 && u >= 0 && u <= 1) {
    return {
      x: rayStart.x + t * r_px,
      y: rayStart.y + t * r_py
    };
  }

  return null; // No valid intersection
}

export function Clamp(me: number, min: number, max: number) {
  return Math.min(Math.max(me, min), max);
}

export function Int2Bin(int: number): string {
  return (int >>> 0).toString(2);
}

export function isEven(n: number) {
  return n % 2 === 0;
}

/**
 * Rounds down to a given digit (i.e. exponent of 10).
 * @param digit - the digit after/before the decimal point to round to.
 * 3 -> 0.00n,
 * 2 -> 0.0n,
 * 1 -> 0.n,
 * 0 -> n,
 * -1 -> n0,
 * -2 -> n00,
 * -3 -> n000, etc.
 */
export function roundDown(number: number, digit: number): string {
  const powerOfTen = 10 ** Math.floor(digit);

  return (Math.floor(number * powerOfTen) / powerOfTen).toString();
}

/**
 * Get a random int between range max and min, which are both inclusive
 * @param min number must be int
 * @param max number must be int
 */
export function randomIntRange(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Uniform random float in the half-open interval [min, max). */
export function randomFloatRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function RoundDecimalNoJank(decimal: number): number {
  return Math.round((decimal + Number.EPSILON) * 100000000) / 100000000;
}

export function CreateArcVertices(startPoint: ICoord2D, angleDeg: number, segmentCount: number): ICoord2D[] {
  if (segmentCount < 1) {
    throw new Error(`Arc must have 1 or more segments/edges`);
  }

  const angleRad = Deg2Rad(angleDeg);

  const verts: ICoord2D[] = [{ x: 0, y: 0 }];
  const segAngleRad: number = angleRad / segmentCount;
  for (let i = 0; i <= segmentCount; i++) {
    const angle = segAngleRad * i;
    verts.push(GeomUtils.Rotate(startPoint, angle));
  }

  return verts;
}

export function average(...values: number[]): number {
  const sum = values.reduce((a, b) => a + b, 0);
  return (sum / values.length) || 0;
}

/**
 * Greatest common divisor of two numbers (Euclidean algorithm).
 * Useful e.g. to reduce aspect ratios to simplest form (1920×1080 → 16:9).
 * Inputs are rounded and absoluted; returns at least 1 when b is 0 to avoid division by zero.
 */
export function gcd(a: number, b: number): number {
  a = Math.round(Math.abs(a));
  b = Math.round(Math.abs(b));
  if (b === 0) { return a || 1; }
  return gcd(b, a % b);
}
